// @flow
import React, { useCallback, useContext, useMemo } from 'react';
import { useIsUserAdmin } from '@src/custom-hooks/useIsUserAdmin';
import { useEstimateMLDForFieldGuidance } from '@src/custom-hooks/useEstimateMLDForFieldGuidance';
import { isCurrentRowDimensionsValidBasedOnUsageAndGuidanceForField } from '@components/custom-discounts/mld-for-field/utils';

import {
  CUSTOM_DISCOUNTS_JSON_BACKEND_NAME,
  METRICS_LEVEL_DISCOUNTS_CONFIG_ARRAY_BACKEND_NAME,
  METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_ARRAY_BACKEND_NAME,
  METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_NAME_BACKEND_NAME,
  RATE_CARD_BACKEND_NAME,
} from '../../../constants';
import {
  FieldArrayGrid,
  FieldArrayGridConfigItemContext,
} from '../../../formik-utils/FieldArrayGrid';
import { DataFetcherContainer } from '../../presentational/DataFetcherContainer';
import { MetricsLevelDiscountsDimensionsConfigurationItem } from '../dimensions-configuration/MetricsLevelDiscountsDimensionsConfigurationItem';
import { getDependentDimensionsColumnConfig } from '../utils';
import { EstimateContext } from '../../../contexts/EstimateContext';
import { useGetDependentDimensionsQuery } from '../../../service-definitions/estimates';

export const MetricsDimensionsListContainer = ({ metricDimensionRefs, readOnlyMode }) => {
  const estimate = useContext(EstimateContext);
  const { rowValues, index } = useContext(FieldArrayGridConfigItemContext);
  const isUserAdmin = useIsUserAdmin();
  const mldGuidance = useEstimateMLDForFieldGuidance();

  const metricName = rowValues[METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_NAME_BACKEND_NAME];

  const pathToFollowInValues = [
    CUSTOM_DISCOUNTS_JSON_BACKEND_NAME,
    `${METRICS_LEVEL_DISCOUNTS_CONFIG_ARRAY_BACKEND_NAME}[${index}]["${METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_ARRAY_BACKEND_NAME}"]`,
  ];

  const rateCardVersion = estimate.inputs[RATE_CARD_BACKEND_NAME];

  return (
    <DataFetcherContainer
      dataFetchingArgs={useMemo(
        () => [
          {
            metricName: metricName,
            rateCardVersion: rateCardVersion,
          },
          {
            skip: !metricName,
          },
        ],
        [rateCardVersion, metricName]
      )}
      dataFetchingFunction={useCallback(useGetDependentDimensionsQuery, [])}
    >
      {(dependentDimensions) => {
        const dependentDimensionsToUse = dependentDimensions?.dependent_dimensions ?? [];
        const columnConfig = getDependentDimensionsColumnConfig(
          dependentDimensionsToUse,
          metricName,
          true,
          true,
          true
        );

        metricDimensionRefs.current.dimensionColumnsConfiguration = columnConfig;

        return (
          <>
            <FieldArrayGrid
              addDividerBetweenRows={false}
              columnsConfiguration={columnConfig}
              disabled={readOnlyMode}
              functionToExecuteOnceArrayHelpersAreAvailable={(arrayHelpers) => {
                metricDimensionRefs.current.dimensionArrayHelpers = arrayHelpers;
              }}
              gridName="New Dimensions Configuration For the above metric"
              needsAddButton={false}
              pathToFollowInValues={pathToFollowInValues}
              verticalSpaceBetweenHeadersAndRows={5}
            >
              {(arrayHelpers, row, isCurrentlyBeingEdited, index, allRows, readOnlyMode) => {
                // Check whether the current row is valid for the current user ( Admin vs non-Admin ) and disable the row if the dimension values are not valid according to the user
                const areDimensionsValidForTheGivenUser =
                  isCurrentRowDimensionsValidBasedOnUsageAndGuidanceForField({
                    rowValues: row,
                    mldGuidance,
                    isUserAdmin,
                  });

                return (
                  <FieldArrayGridConfigItemContext.Provider
                    value={{
                      allRows,
                      arrayHelpers,
                      columnsConfiguration: columnConfig,
                      estimate,
                      index,
                      isCurrentlyBeingEdited,
                      pathToFollowInValues: pathToFollowInValues,
                      rowValues: row,
                      readOnlyMode: readOnlyMode || !areDimensionsValidForTheGivenUser,
                      readOnlyModeForDeleteButton: readOnlyMode,
                      parentContext: rowValues,
                      metricDimensionRefs: metricDimensionRefs,
                    }}
                  >
                    <MetricsLevelDiscountsDimensionsConfigurationItem />
                  </FieldArrayGridConfigItemContext.Provider>
                );
              }}
            </FieldArrayGrid>
          </>
        );
      }}
    </DataFetcherContainer>
  );
};
